import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PackageCheck, ScanLine } from 'lucide-react'
import { getLocations } from '../../api/master'
import { getPO, getPORemaining, listPOs, saveReceipt } from '../../api/purchase'
import { enqueuePutawayForReceipt } from '../../api/inventory'
import { getConfig } from '../../api/settings'
import { useToast } from '../../components/Toast.jsx'
import ErrorBanner from '../../components/ErrorBanner'
import BarcodeScanner from '../../components/BarcodeScanner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { num } from '../sales/salesShared'
import { CreateOption } from '../../components/CreateOption'

const isOn = (v) => [true, 1, '1', 'true', 'yes', 'on'].includes(v)

// Receive goods against a purchase order: choose the order and its
// outstanding items fill in with what is still to come. Or count them by
// scanning: every line starts at 0 and each scan of a product's barcode (or
// SKU) adds one; the scanner opens again until you close it (in the phone
// app it's the phone's own scanner; a handheld scanner types into the box).
export default function ReceiptForm() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const { notify } = useToast()
  const [pos, setPos] = useState([])
  const [locations, setLocations] = useState([])
  const [poId, setPoId] = useState(params.get('po_id') || '')
  const [locationId, setLocationId] = useState('')
  const [notes, setNotes] = useState('')
  const [items, setItems] = useState([]) // { product_id, sku, name, ordered, remaining, qty }
  const [poHeader, setPoHeader] = useState(null)
  const [posLoaded, setPosLoaded] = useState(false)
  const [loadingItems, setLoadingItems] = useState(false)
  const [binTracking, setBinTracking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(null) // { receiptId, skus }
  const [error, setError] = useState('')
  const [counting, setCounting] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [scanned, setScanned] = useState(null) // { text, tone, productId }
  const [code, setCode] = useState('')
  const reopen = useRef(null)
  useEffect(() => () => clearTimeout(reopen.current), [])

  useEffect(() => {
    listPOs({ eligible_for_receipt: 1 })
      .then((all) => setPos((Array.isArray(all) ? all : []).filter((p) => ['', 'APPROVED', 'NOTREQUIRED'].includes(String(p.approval_status || '').trim().replace(/\s+/g, '').toUpperCase()))))
      .catch(() => setPos([]))
      .finally(() => setPosLoaded(true))
    getLocations().then((ls) => { setLocations(ls || []); if (ls?.length) setLocationId(String(ls[0].location_id)) }).catch(() => {})
    getConfig().then((c) => setBinTracking(isOn(c?.bin_tracking))).catch(() => {})
  }, [])

  // The chosen order's items, with what is still to come pre-filled.
  useEffect(() => {
    setCounting(false); setScanned(null)
    if (!poId) { setItems([]); setPoHeader(null); return }
    let cancelled = false
    setLoadingItems(true)
    Promise.all([getPO(poId), getPORemaining(poId, { net: false }).catch(() => [])])
      .then(([po, rem]) => {
        if (cancelled) return
        setPoHeader(po?.header || null)
        const left = Object.fromEntries((rem || []).map((r) => [String(r.product_id), num(r.remaining)]))
        setItems((po?.lines || []).map((l) => {
          const remaining = left[String(l.product_id)] ?? num(l.quantity)
          return { product_id: l.product_id, sku: l.sku, barcode: l.barcode || '', name: l.name, ordered: num(l.quantity), remaining, qty: remaining > 0 ? String(remaining) : '' }
        }))
      })
      .catch(() => { if (!cancelled) setItems([]) })
      .finally(() => { if (!cancelled) setLoadingItems(false) })
    return () => { cancelled = true }
  }, [poId])

  const po = pos.find((p) => String(p.po_id) === String(poId))
  const blocked = posLoaded && poId && !po
  const receiving = items.filter((i) => num(i.qty) > 0)
  const sortedPos = useMemo(() => [...pos].sort((a, b) => num(b.po_id) - num(a.po_id)), [pos])

  // Count by scanning: from 0, one per scan.
  const startCounting = () => {
    if (!counting) {
      setItems((xs) => xs.map((x) => ({ ...x, qty: '' })))
      setCounting(true)
      setScanned(null)
    }
    setScanning(true)
  }
  const addScan = (raw) => {
    const c = String(raw || '').trim().toLowerCase()
    if (!c) return
    const it = items.find((x) => [x.barcode, x.sku].some((v) => v && String(v).trim().toLowerCase() === c))
    if (!it) {
      setScanned({ text: `${raw} isn’t on this order.`, tone: 'warn', productId: null })
      return
    }
    const now = num(it.qty) + 1
    setItems((xs) => xs.map((x) => (x.product_id === it.product_id ? { ...x, qty: String(now) } : x)))
    const name = it.name || it.sku
    setScanned(now > it.remaining + 1e-9
      ? { text: `${name}: ${now}, more than the ${it.remaining} still due.`, tone: 'warn', productId: it.product_id }
      : { text: `${name}: ${now} of ${it.remaining}`, tone: 'good', productId: it.product_id })
  }
  const onScan = (c) => {
    setScanning(false)
    addScan(c)
    reopen.current = setTimeout(() => setScanning(true), 450)
  }
  const typed = (e) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    addScan(code)
    setCode('')
  }

  const save = async () => {
    setError('')
    if (!poId) { setError('Choose the purchase order these goods are for.'); return }
    if (blocked) { setError('This order can’t be received: it is waiting for approval, was rejected, or is closed.'); return }
    if (!locationId) { setError('Choose where the goods are going.'); return }
    if (!receiving.length) { setError('Enter how many of each item arrived.'); return }
    const over = receiving.find((i) => num(i.qty) > i.remaining + 1e-9)
    if (over) { setError(`More ${over.name || over.sku} than is still due (${over.remaining}). Check the quantity or the order.`); return }
    setSaving(true)
    try {
      const res = await saveReceipt({
        po_id: parseInt(poId, 10),
        vendor_id: parseInt(poHeader?.vendor_id ?? po?.vendor_id, 10),
        location_id: parseInt(locationId, 10),
        notes,
        lines: receiving.map((i) => ({ product_id: parseInt(i.product_id, 10), quantity: num(i.qty) })),
      })
      notify('Goods received')
      if (binTracking) {
        setSaved({ receiptId: res?.receipt_id || null, skus: [...new Set(receiving.map((i) => i.sku).filter(Boolean))] })
      } else {
        nav(res?.receipt_id ? `/purchase/receipts/${res.receipt_id}` : '/purchase/receipts')
      }
    } catch (e) {
      setError(e.message || 'Could not save the receipt.')
    } finally {
      setSaving(false)
    }
  }

  if (saved) {
    return (
      <div className="ci-form-page">
        <DocHeader back={<BackLink to="/purchase/receipts" label="Receiving" />} title="Goods received" />
        <section className="doc-card doc-done">
          <PackageCheck size={28} aria-hidden="true" />
          <div>
            <h2>Put the stock away</h2>
            <p>Move what arrived into its bins now, or leave it in the put-away queue for later.</p>
          </div>
          <div className="doc-done-actions">
            <button type="button" className="ci-btn ci-btn-ghost" onClick={async () => { try { if (saved.receiptId) await enqueuePutawayForReceipt(saved.receiptId) } catch { /* the queue is best effort */ } nav(saved.receiptId ? `/purchase/receipts/${saved.receiptId}` : '/purchase/receipts') }}>Later</button>
            <Link className="ci-btn ci-btn-primary" to={`/inventory/bin-move-sku?skus=${encodeURIComponent(saved.skus.join(','))}`}>Put away now</Link>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/purchase/receipts" label="Receiving" />} title="Receive goods" meta="Choose the purchase order, then check how many of each item arrived." />

      {error && <ErrorBanner message={error} />}
      {blocked && <p className="doc-note">This order can’t be received yet: it is waiting for approval, was rejected, or is closed.</p>}

      <div className="ci-form-meta-card">
        <div className="ci-form-meta-top two">
          <label className="ci-form-field">
            <span>Purchase order</span>
            <select value={poId} onChange={(e) => setPoId(e.target.value)}>
              <option value="">Choose an order…</option>
              {sortedPos.map((p) => <option key={p.po_id} value={p.po_id}>{docNo('po', p.po_no)} · {p.vendor_name || 'Vendor'}{p.po_date ? ` · ${fmtDay(p.po_date)}` : ''}</option>)}
            </select>
          </label>
          <label className="ci-form-field">
            <span>Received at</span>
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              <CreateOption kind="location" />
            </select>
          </label>
        </div>
      </div>

      <div className="ci-form-lines-card">
        <div className="ci-form-lines-head">
          <div className="ci-form-lines-title">What arrived</div>
          <span>{poHeader?.vendor_name ? `From ${poHeader.vendor_name}` : ''}</span>
          {items.some((i) => i.remaining > 0) && (
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm rc-scan-btn" onClick={startCounting}>
              <ScanLine size={14} />{counting ? 'Scan more' : 'Scan what arrived'}
            </button>
          )}
        </div>
        {counting && (
          <div className="rc-scan">
            <p className="doc-hint">Counting by scan: each scan adds one. Correct any line by typing in it.</p>
            <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={typed} placeholder="Scan or type a barcode or SKU, then press Enter" aria-label="Product code" />
            {scanned && <p className={`rc-scan-msg ${scanned.tone}`} role="status">{scanned.text}</p>}
          </div>
        )}
        {!poId ? (
          <p className="doc-empty">Choose a purchase order above to see what’s due.</p>
        ) : loadingItems ? (
          <p className="doc-empty">Loading the order…</p>
        ) : items.length === 0 ? (
          <p className="doc-empty">This order has no items.</p>
        ) : (
          <div className="ci-form-table-wrap">
            <table className="ci-form-line-table compact">
              <thead>
                <tr><th>Item</th><th className="num">Ordered</th><th className="num">Still due</th><th className="num col-price">Arrived now</th></tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.product_id} className={scanned?.productId === it.product_id ? 'rc-last' : undefined}>
                    <td className="ci-form-cell-text">{it.name || it.sku}<span className="cell-sub">{it.sku}</span></td>
                    <td className="ci-form-cell-num">{it.ordered.toLocaleString()}</td>
                    <td className="ci-form-cell-num">{it.remaining > 0 ? it.remaining.toLocaleString() : 'All received'}</td>
                    <td>
                      <input
                        className="ci-form-cell-input num"
                        value={it.qty}
                        inputMode="decimal"
                        placeholder="0"
                        disabled={it.remaining <= 0}
                        onChange={(e) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                        aria-label={`Quantity of ${it.name || it.sku} that arrived`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BarcodeScanner open={scanning} onClose={() => { clearTimeout(reopen.current); setScanning(false) }} onScan={onScan} />

      <label className="ci-form-field ci-form-note">
        <span>Note (optional)</span>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Two cartons damaged, delivery note #4471" />
      </label>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving || !receiving.length} onClick={save}>
            <PackageCheck size={14} />{saving ? 'Saving…' : `Receive ${receiving.length ? `${receiving.length} ${receiving.length === 1 ? 'item' : 'items'}` : 'goods'}`}
          </button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/purchase/receipts')}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
